import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';

import { Ripple } from "primereact/ripple";
import { Dialog } from 'primereact/dialog';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from "primereact/inputtext";
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";

import NoDataCreated from "../../../../Components/NoDataCreated";
import TextInput from "../../../../Components/TextInput";
import MulipleFileInput from "../../../../Components/MulipleFileInput";
import DocumentLoading from "../../../../Components/DocumentLoading";
import ErrorPage from "../../../../Components/ErrorPage";
import FileInput from "../../../../Components/FileInput";
import PageLoader from "../../../../Components/PageLoader";
import { goToTop } from "../../../../Components/goToTop";

import { checkFileOtherList, checkFileOthers, checkText } from "../../../../Utils/Validator";
import { drawingTypeCreateAPI, drawingTypeGetDataAPI, drawingTypeListDataAPI } from "../../../../Api/Project/Drawing/DrawingType";
import { DrawingCollections, DrawingDefaultFolderFiles, DrawingType } from "../../../../Controllers/Types/Project/Service/DrawingInterface";
import { drawingCollectionCreateAPI, drawingCollectionDeleteAPI, drawingCollectionGetDataAPI, drawingCollectionUpdateAPI, FolderdrawingCollectionGetDataAPI } from "../../../../Api/Project/Drawing/DrawingCollection";
import { baseURL } from "../../../../Api/ApiConstant";
import packageJson from '../../../../../../package.json';

const DrawingDefaultFolder: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const projectData = location.state?.data;
    const subFolderId = projectData.subFolderId;
    const folderId = projectData.folder_id;
    const folderType = projectData.folder_type;
    const isShared: boolean = projectData.sharedBy !== null ? true : false;

    const toast = useRef<Toast>(null);
    const [loadingPage, setLoadingPage] = useState<boolean>(false);
    const error = '';
    const userId: number = Number(localStorage.getItem('user_id')) || 0;

    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [loading, setLoading] = useState<boolean>(false);
    const [showAddCustomData, setShowAddCustomData] = useState<boolean>(false);

    const [viewDocumentFullscreen, setViewDocumentFullScreen] = useState(false);
    const fileViewerRef = useRef<HTMLDivElement>(null);
    const loadingtiles = Array.from({ length: 5 });
    const [contentLoading, setContentLoading] = useState<boolean>(false);

    const defaultLayoutPluginInstance = defaultLayoutPlugin();
    const [fileUrl, setFileUrl] = useState<string>('');
    const [selectedDrawing, setSelectedDrawing] = useState<DrawingCollections | null>(null);
    const [selectedDrawingCollection, setSelectedDrawingCollection] = useState<DrawingCollections | null>(null);
    const pdfjsVersion = packageJson.dependencies['pdfjs-dist'].replace('^', '');

    /* ************************ custom drawing type ********************************** */
    const [customDrawingType, setCustomDrawingType] = useState<string>('');
    const [drawingType, setDrawingType] = useState<DrawingType[]>([]);
    const [selectedDrawingType, setSelectedDrawingType] = useState<DrawingType | null>(null);
    const [errorCustomDrawingType, setErrorCustomDrawingType] = useState<string>('');
    /* ************************ custom drawing type ********************************** */

    /* ************************ tab bar ********************************** */
    const [selectedDrawingTypeTab, setSelectedDrawingTypeTab] = useState<DrawingType | null>(null);
    /* ************************ tab bar ********************************** */

    /* ************************ drawing collection data ********************************** */
    const [drawingCollectionData, setDrawingCollection] = useState<DrawingDefaultFolderFiles[]>([]);
    /* ************************ drawing collection data ********************************** */

    /* ****************************** For Document upload ****************************** */
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [drawingTitle, setDrawingTitle] = useState<string>('');
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [updateUploadedFile, setUpdateUploadedFile] = useState<File | null>(null);
    const [uploadedFileName, setUploadedFileName] = useState<string>('');

    /* For error */
    const [errorSelectedDrawingType, setErrorSelectedDrawingType] = useState<string>('');
    const [errorDrawingTitle, setErrorDrawingTitle] = useState<string>('');
    const [errorFileUploadError, setErrorFileUpload] = useState<string>('');

    const [drawingUpdateHasChanges, setDrawingUpdateHasChanges] = useState<boolean>(false);

    const [dataAddLoading, setDataAddLoading] = useState<boolean>(false);

    useEffect(() => {
        setContentLoading(true);

        setStateWithDelay(() => {
            if (!drawingType.length || !drawingCollectionData.length) {
                setSelectedDrawing(null);
                setFileUrl('');
                setSelectedDrawingCollection(null);
                return;
            }

            const initialDrawing = drawingCollectionData
                .find(d => d.drawing_type_id === drawingType[0]?.drawing_type_id)
                ?.drawing_collection?.[0];

            if (initialDrawing) {
                setSelectedDrawing(initialDrawing);
                setFileUrl(baseURL + initialDrawing.path);
                setSelectedDrawingCollection(initialDrawing);
            } else {
                setSelectedDrawing(null);
                setFileUrl('');
                setSelectedDrawingCollection(null);
            }
        });
    }, [drawingCollectionData, drawingType]);

    const handleOpenUploadModal = () => {
        setShowUploadModal(true);
        setDataState('Add');

        setSelectedDrawingType(selectedDrawingTypeTab);
    };

    useEffect(() => {
        if (selectedDrawingCollection) {
            if (drawingTitle !== selectedDrawingCollection?.drawing_name) {
                setDrawingUpdateHasChanges(true);
            } else if (updateUploadedFile !== null) {
                setDrawingUpdateHasChanges(true);
            } else if (selectedDrawingType?.drawing_type_id !== selectedDrawingCollection?.drawing_type_id) {
                setDrawingUpdateHasChanges(true);
            } else {
                setDrawingUpdateHasChanges(false);
            }
        }
    }, [selectedDrawingCollection, drawingTitle, updateUploadedFile, selectedDrawingType]);

    const handleOpenEditModal = (document: DrawingCollections) => {
        setShowUploadModal(true);
        setDataState('Edit');
        const updateDrawingType = drawingType.filter(drawing => drawing.drawing_type_id === document.drawing_type_id)[0];
        setSelectedDrawingType(updateDrawingType);
        setDrawingTitle(document.drawing_name);
    };

    const handleCloseModal = () => {
        setShowUploadModal(false);
        handleClearFields();
        handleClearErrors();
    };

    const handleFilesUpload = (files: File[]) => {
        setUploadedFiles(files);
    };

    const handleFileUpload = (file: File) => {
        setUploadedFileName(file.name);
        setUpdateUploadedFile(file);
    };

    const handleClearFiles = () => {
        setUploadedFiles([]);
    };

    const handleClearFile = () => {
        setUpdateUploadedFile(null);
        setUploadedFileName('');
    };

    /* *************************************************************************************************************************************************** */
    useEffect(() => {
        drawingTypeDataGet();
        drawingCollectionDataGet();
    }, []);

    /* ****************************** drawing type data get ********************* */
    // const drawingTypeDataGet = async () => {
    //     const data = await drawingTypeGetDataAPI();
    //     if (data.status) {
    //         setDrawingType(data.data || []);
    //         const drawingTypeList: DrawingType[] = data.data || [];
    //         if (drawingTypeList.length > 0) {
    //             setSelectedDrawingTypeTab(drawingTypeList[0]);
    //         }
    //     }
    // }

    const drawingTypeDataGet = async () => {
        const data = await drawingTypeListDataAPI(
            isShared === false ? "User" : folderType === 'Doc Drawing' ? "Sub Folder" : "Folder",
            isShared === false ? userId : null,
            folderType === 'Drawing' ? folderId : null,
            folderType === 'Doc Drawing' ? subFolderId : null
        );
        if (data.status) {
            setDrawingType(data.data || []);
            const drawingTypeList: DrawingType[] = data.data || [];
            if (drawingTypeList.length > 0) {
                setSelectedDrawingTypeTab(drawingTypeList[0]);
            }
        }
    }
    /* ****************************** drawing type data get ********************* */


    /* ****************************** drawing collection data get ********************* */
    const drawingCollectionDataGet = async () => {
        if (folderType === "Doc Drawing") {
            setLoadingPage(true);

            const data = await drawingCollectionGetDataAPI(Number(subFolderId));
            if (data.status) {
                setLoadingPage(false);
                setDrawingCollection(data.data || []);
            }
        } else {
            setLoadingPage(true);

            const data = await FolderdrawingCollectionGetDataAPI(Number(folderId));
            if (data.status) {
                setLoadingPage(false);
                setDrawingCollection(data.data || []);
            }
        }
    }
    /* ****************************** drawing collection data get ********************* */
    /* *************************************************************************************************************************************************** */

    /* ************************************** drawing type create part - start ************************************* */
    useEffect(() => {
        checkText(customDrawingType, false, setErrorCustomDrawingType)
    }, [customDrawingType])

    const handleAddDrawingType = async (e: React.FormEvent) => {
        e.preventDefault();

        setErrorCustomDrawingType('');
        if (checkText(customDrawingType, true, setErrorCustomDrawingType)) {
            setDataAddLoading(true);
            const data = await drawingTypeCreateAPI(customDrawingType);

            setDataAddLoading(false);
            if (data.status) {
                if (toast.current) {
                    drawingTypeDataGet();
                    setCustomDrawingType('');
                    setShowAddCustomData(false);
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Drawing type created successfully.',
                        life: 3000
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Error creating drawing type.',
                        life: 3000
                    });
                }
                if (data.statusCode === 422) {
                    Object.entries(data.errors ?? []).map(([key, value], index) => (
                        setErrorCustomDrawingType(value[0])
                    ))
                }
            }
        }
    }

    /* ************************************** drawing type create part - start ************************************* */

    /* ************* clear input *********************** */
    const handleClearFields = () => {
        setUploadedFiles([]);
        setSelectedDrawingType(null);
        setDrawingTitle('');
        setUpdateUploadedFile(null);
        setUploadedFileName('');
    }
    /* ************* clear input *********************** */

    /* ************* clear error massage *********************** */
    const handleClearErrors = () => {
        setErrorSelectedDrawingType('');
        setErrorDrawingTitle('');
        setErrorFileUpload('');
    }
    /* ************* clear error massage *********************** */


    /* ************************************** drawing detail create part - start ************************************* */
    useEffect(() => {
        checkText(selectedDrawingType?.type!, false, setErrorSelectedDrawingType);
    }, [selectedDrawingType]);

    useEffect(() => {
        checkText(drawingTitle, false, setErrorDrawingTitle);
    }, [drawingTitle]);

    useEffect(() => {
        checkFileOtherList(uploadedFiles, false, setErrorFileUpload);
    }, [uploadedFiles]);

    useEffect(() => {
        checkFileOthers(updateUploadedFile!, false, setErrorFileUpload);
    }, [updateUploadedFile]);

    function checkCredentials(): boolean {
        const isSelectedDrawingTypeValid = checkText(selectedDrawingType?.type || '', true, setErrorSelectedDrawingType);
        const isDrawingTitleValid = checkText(drawingTitle, true, setErrorDrawingTitle);

        if (dataState === "Add") {
            const isUploadedFileValid = checkFileOtherList(uploadedFiles, true, setErrorFileUpload);

            return isSelectedDrawingTypeValid && isDrawingTitleValid && isUploadedFileValid;
        } else { // update
            const isUploadedFileValid = checkFileOthers(updateUploadedFile!, false, setErrorFileUpload);
            return isSelectedDrawingTypeValid && isDrawingTitleValid && isUploadedFileValid;
        }
    }

    const handleCreateDrawingDetail = async (e: React.FormEvent) => {
        e.preventDefault();

        var forCreate = null;
        if (folderType === "Doc Drawing") {
            forCreate = "Sub Folder";
        } else {
            forCreate = "Folder";
        }

        if (checkCredentials()) {
            setLoading(true);
            const data = await drawingCollectionCreateAPI(forCreate, Number(folderId), Number(subFolderId), selectedDrawingType?.drawing_type_id || 0, drawingTitle, uploadedFiles);
            setLoading(false);

            if (data.statusCode === 200) {
                drawingCollectionDataGet();

                handleCloseModal();

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: `${projectData.sub_folder_name ? projectData.sub_folder_name : ''} Document added successfully.`,
                        life: 3000
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error uploading document.',
                        detail: 'There was an error uploading document.',
                        life: 3000
                    });
                }
            }
        } else {
        }
    };
    /* ************************************** drawing detail create part - end ************************************* */

    /* ************************************** drawing detail update part - start ************************************* */
    const handleUpdateDrawingDetail = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials()) {
            setLoading(true);
            const data = await drawingCollectionUpdateAPI(selectedDrawingCollection?.id || 0, selectedDrawingType?.drawing_type_id || 0, drawingTitle, updateUploadedFile!);
            setLoading(false);

            if (data.statusCode === 200) {
                drawingCollectionDataGet();

                handleCloseModal();

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: `${projectData.sub_folder_name ? projectData.sub_folder_name : ''} Document updated successfully.`,
                        life: 3000
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error uploading document.',
                        detail: 'There was an error uploading document.',
                        life: 3000
                    });
                }
            }
        } else {
        }
    };
    /* ************************************** drawing detail update part - end ************************************* */


    /* ************************************** drawing detail delete part - start ************************************* */
    const handleDeletedocumentData = (document: DrawingCollections): void => {
        confirmDialog({
            message: 'Are you sure you want to delete Document detail?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteDocumentData(document.id),
            resizable: false,
            draggable: false,
            dismissableMask: true,
        });
    }

    // delete operation
    const deleteDocumentData = async (documentId: number) => {
        const data = await drawingCollectionDeleteAPI(documentId);

        if (data.statusCode === 200) {
            drawingCollectionDataGet();

            setSelectedDrawing(null);
            setFileUrl('');
            setSelectedDrawingCollection(null);

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: `${projectData.service_name ? projectData.service_name : ''} Document deleted successfully.`,
                    life: 3000
                });
            }

            setShowUploadModal(false);
        }
    }
    /* ************************************** drawing detail delete part - end ************************************* */



    /* Modal header */
    const drawingModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp;{projectData.service_name} detail
            </h5>
            {dataState === 'Edit' ? (
                <button type="button" className="close_modal_btn p-ripple"
                    onClick={() => handleDeletedocumentData(selectedDrawingCollection!)}>
                    <i className="bi bi-trash3"></i>
                    <Ripple />
                </button>
            ) : (
                <button type="button" className="close_modal_btn p-ripple"
                    onClick={handleCloseModal}>
                    <i className="bi bi-x-circle"></i>
                    <Ripple />
                </button>
            )}
        </div>
    );

    /* Modal footer */
    const drawingModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateDrawingDetail : handleUpdateDrawingDetail}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !drawingUpdateHasChanges}
            />
        </div>
    );

    /* ********************************************************************************* */

    /* ******************************* For view document ******************************* */
    const handleOpenDocumentViewModal = () => {
        setViewDocumentFullScreen(true);
    };

    const handleCloseDocumentViewModal = () => {
        setViewDocumentFullScreen(false);
    };

    const headerTemplate = (
        <div className={`document_modal_header_inner'`}>
            <button type="button" className="close_modal_btn primary p-ripple p-0"
                onClick={handleCloseDocumentViewModal}>
                <i className="bi bi-fullscreen-exit"></i>
                <Ripple />
            </button>
        </div>
    )

    const setStateWithDelay = (callback: () => void) => {
        setTimeout(() => {
            callback();
            setContentLoading(false);
        }, 1200);
    };



    const handleViewDrawingTypeDocuments = (drawingType: DrawingType) => {
        setSelectedDrawingTypeTab(drawingType);
        setContentLoading(true);
        setStateWithDelay(() => {
            const drawing = drawingCollectionData.find(d => d.drawing_type_id === drawingType.drawing_type_id)?.drawing_collection[0];

            if (drawing) {
                setSelectedDrawing(drawing);
                setFileUrl(baseURL + drawing.path);
                setSelectedDrawingCollection(drawing);
            } else {
                setSelectedDrawing(null);
                setFileUrl('');
                setSelectedDrawingCollection(null);
            }
        });
    };

    const handleSelectFile = (drawing: DrawingCollections) => {
        setSelectedDrawing(drawing);
        setFileUrl(baseURL + drawing.path);
        setSelectedDrawingCollection(drawing);
    };

    /* ********************************************************************************* */

    const toggleAddCustomData = () => {
        setShowAddCustomData(!showAddCustomData);
    };

    const goBack = () => {
        navigate(-1);
        goToTop();
    };

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingPage ? (
                    <PageLoader />
                ) : error ? (
                    <ErrorPage errorMessage={error} />
                ) : (
                    <div>
                        <div className="page_content mt-0">
                            <div className="page_breadcrumb_area">
                                <ul className="page_breadcrumb">
                                    <li className="page_breadcrumb_item has_child has_link" onClick={() => folderType === 'Doc Drawing' ? navigate(-2) : navigate(-1)}>
                                        {projectData.job_number} : {projectData.address}
                                    </li>

                                    <li className="page_breadcrumb_item has_child">
                                        {projectData.folder}
                                    </li>

                                    <li className={`page_breadcrumb_item ${folderType === 'Doc Drawing' ? 'has_child has_link' : 'active'} `} onClick={() => navigate(-1)}>
                                        {projectData.service_name}
                                    </li>

                                    {folderType === 'Doc Drawing' && (
                                        <li className="page_breadcrumb_item active">
                                            {projectData.sub_folder_name}
                                        </li>
                                    )}
                                </ul>

                                <button className="back_btn p-ripple" onClick={goBack}>
                                    <i className="bi bi-arrow-left me-2"></i>
                                    Back
                                    <Ripple />
                                </button>

                            </div>
                            <div className="page_header_area mt-sm-2 justify-content-end">
                                {isShared === false && (
                                    <div className="page_action_area">
                                        <button className="add_data_link_btn p-ripple" onClick={handleOpenUploadModal}>
                                            <i className="bi bi-arrow-up-circle"></i>
                                            <span>Upload file</span>
                                            <Ripple />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {drawingCollectionData?.length > 0 ? (
                                <div className="page_split_content_section">
                                    <div className="row">
                                        <div className="col-12 col-xl-8">
                                            <div className="document_nav_bar_section">
                                                {drawingCollectionData?.length > 0 && (
                                                    <ul className="document_nav_tabs_area" id="document_nav_tabs" role="tablist">
                                                        {drawingCollectionData.map((drawingType) => (
                                                            <li className="document_nav_tab_item" key={drawingType.drawing_type_id}>
                                                                <button type="button" onClick={() => handleViewDrawingTypeDocuments(drawingType)}
                                                                    className={`document_nav_tab_btn p-ripple ${selectedDrawingTypeTab?.drawing_type_id === drawingType.drawing_type_id ? 'active' : ''}`}>
                                                                    {drawingType.type}
                                                                    <Ripple />
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}

                                                <div className="document_nav_bar_action_btn_area">
                                                    {fileUrl && (
                                                        <button className="document_nav_bar_action_btn p-ripple"
                                                            onClick={handleOpenDocumentViewModal}>
                                                            <i className="bi bi-arrows-fullscreen"></i>
                                                            <Ripple />
                                                        </button>
                                                    )}

                                                    {isShared === false && selectedDrawingCollection && (
                                                        <button className="document_nav_bar_action_btn p-ripple"
                                                            onClick={() => handleOpenEditModal(selectedDrawingCollection)}>
                                                            <i className="bi bi-pencil-square"></i>
                                                            <Ripple />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row document_view_row">
                                        <div className="col-12 col-xl-8 col-lg-7 col-md-7 col-sm-7 pe-sm-2" id="fileViewer" ref={fileViewerRef}>
                                            <div className="drawing_files_view_area">
                                                {contentLoading ? (
                                                    <DocumentLoading />
                                                ) : (
                                                    <React.Fragment>
                                                        {fileUrl ? (
                                                            <Worker workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`}>
                                                                <Viewer fileUrl={fileUrl} plugins={[defaultLayoutPluginInstance]} />
                                                            </Worker>
                                                        ) : (
                                                            <div className="no_document_available_area">
                                                                <img src="/assets/images/no_data/no_document.png" alt="No document available." />
                                                                <p>No document available.</p>
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-12 col-xl-4 col-lg-5 h-100 col-md-5 col-sm-5 ps-sm-2">
                                            <div className="drawing_files_list_container">
                                                <div className="drawing_files_list_area">
                                                    {contentLoading ? (
                                                        <React.Fragment>
                                                            {loadingtiles.map((_, index) => (
                                                                <Skeleton key={index} className="file_view_loading"></Skeleton>
                                                            ))}
                                                        </React.Fragment>
                                                    ) : (
                                                        <React.Fragment>
                                                            {drawingCollectionData
                                                                .find(d => d.drawing_type_id === selectedDrawingTypeTab?.drawing_type_id)?.drawing_collection.length === 0 ? (
                                                                <div className="no_ducument_list">
                                                                    <img src="/assets/images/no_data/no_documents.png" alt="No Documents available" />
                                                                    <p>No documents available</p>
                                                                </div>
                                                            ) : (
                                                                drawingCollectionData
                                                                    .find(d => d.drawing_type_id === selectedDrawingTypeTab?.drawing_type_id)?.drawing_collection
                                                                    .map((drawing, index) => (
                                                                        <article
                                                                            key={drawing.id}
                                                                            className={`drawing_files_list_option p-ripple ${selectedDrawing?.id === drawing.id ? 'active' : ''}`}
                                                                            onClick={() => handleSelectFile(drawing)}>
                                                                            <div className="drawing_files_list_op_header">
                                                                                <h6 className="drawing_files_list_no">{String(index + 1).padStart(2, '0')}</h6>
                                                                                <h6 className="drawing_files_list_date">{drawing.update_at}</h6>
                                                                            </div>
                                                                            <h6 className="drawing_files_list_op_title">{drawing.drawing_name}</h6>
                                                                            <div className="drawing_files_list_icon">
                                                                                <i className="bi bi-collection-fill"></i>
                                                                            </div>
                                                                            <Ripple />
                                                                        </article>
                                                                    ))
                                                            )}
                                                        </React.Fragment>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="page_main_content_section">
                                    <NoDataCreated
                                        message={`No any files added inside ${projectData.service_name} sub folder ${!isShared ? 'To add the file click on upload file button' : ''}`}
                                    />
                                </div>
                            )}

                        </div>
                    </div>
                )}

                {/* Drawing detail modal */}
                <Dialog
                    visible={showUploadModal}
                    header={drawingModalHeader}
                    footer={drawingModalFooter}
                    headerClassName="custom_modal_header"
                    className="custom_modal_dialog modal_dialog_sm"
                    onHide={handleCloseModal}
                    dismissableMask
                >
                    <div className="custom_modal_body">
                        <div className="p-1 p-sm-3 pt-sm-2 pb-sm-0 w-100">
                            <div className="row">
                                {/* Draying type */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <div className="w-100">
                                            <label htmlFor="drawingType" className="custom_form_label is_required">Drawing type</label>
                                            <Dropdown
                                                value={selectedDrawingType}
                                                id="drawingType"
                                                onChange={(e: DropdownChangeEvent) => setSelectedDrawingType(e.value)}
                                                options={drawingType}
                                                optionLabel="type"
                                                filter
                                                filterPlaceholder="Search here"
                                                placeholder="Select drawing type"
                                                className="form_dropdown w-100"
                                                showClear
                                                emptyMessage={
                                                    <span>
                                                        No drawing types have been added. <br /> To add, click the Add button.
                                                    </span>
                                                }
                                            />
                                            {errorSelectedDrawingType &&
                                                <small className="form_error_msg">{errorSelectedDrawingType}</small>
                                            }
                                        </div>
                                        <div className="add_data_area">
                                            <button className={`add_data_btn ${showAddCustomData ? 'danger' : ''} p-ripple`} onClick={toggleAddCustomData}>
                                                <i className={`bi ${showAddCustomData ? 'bi-dash-circle' : ' bi-plus-circle'}`}></i>
                                                {showAddCustomData ? (<span>Cancel</span>)
                                                    : (<span>Add Drawing type</span>)
                                                }
                                                < Ripple />
                                            </button>

                                            <div className={`add_data_input_area ${showAddCustomData ? 'show' : ''}`}>
                                                <InputText
                                                    id="customDrawingType"
                                                    value={customDrawingType}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomDrawingType(e.target.value)}
                                                    className="custom_form_input"
                                                    placeholder="Enter drawing type"
                                                />
                                                <Button
                                                    label="Add"
                                                    className="dwn_data_add_btn"
                                                    onClick={handleAddDrawingType}
                                                    loading={dataAddLoading}
                                                    disabled={customDrawingType === ''}
                                                />
                                            </div>
                                            {errorCustomDrawingType &&
                                                <small className="form_error_msg">{errorCustomDrawingType}</small>
                                            }
                                        </div>
                                    </div>
                                </div>

                                {/* Drawing title */}
                                <div className="col-12">
                                    <TextInput
                                        id="drawingTitle"
                                        label="Drawing title/Description"
                                        labelHtmlFor="drawingTitle"
                                        required={true}
                                        value={drawingTitle}
                                        placeholder="Enter drawing title or description"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDrawingTitle(e.target.value)}
                                        error={errorDrawingTitle}
                                    />
                                </div>

                                {/* Upload file */}
                                {dataState === "Add" ? (
                                    <div className="col-12">
                                        <MulipleFileInput
                                            id="uploadedFiles"
                                            label="Upload file(s)"
                                            labelHtmlFor="uploadedFiles"
                                            required={true}
                                            value={uploadedFiles.map(file => file.name).join(', ')}
                                            onUpload={handleFilesUpload}
                                            onClear={handleClearFiles}
                                            toast={toast}
                                            containerClassName="mb-0"
                                            error={errorFileUploadError}
                                        />
                                    </div>
                                ) : (
                                    <div className="col-12">
                                        <FileInput
                                            id="uploadedFile1"
                                            label="Upload file"
                                            labelHtmlFor="uploadedFile1"
                                            required={false}
                                            value={uploadedFileName}
                                            onUpload={handleFileUpload}
                                            onClear={handleClearFile}
                                            toast={toast}
                                            containerClassName="mb-0"
                                            error={errorFileUploadError}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Dialog>


                {/* Document view modal */}
                <Dialog
                    headerClassName="document_modal_header"
                    header={headerTemplate}
                    visible={viewDocumentFullscreen}
                    className="custom_modal_dialog full_view_mode"
                    onHide={handleCloseDocumentViewModal}
                    contentClassName="pt-0"
                    dismissableMask
                >
                    {fileUrl ? (
                        <Worker workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`}>
                            <Viewer fileUrl={fileUrl} plugins={[defaultLayoutPluginInstance]} />
                        </Worker>
                    ) : (
                        <div className="no_document_available_area">
                            <img src="/assets/images/no_data/no_document.png" alt="No document available." />
                            <p>No document available.</p>
                        </div>
                    )}
                </Dialog>
            </div >
        </React.Fragment>
    )
}

export default DrawingDefaultFolder;